import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState
} from 'react'
import { Helmet } from 'react-helmet'
import { ThemeProvider, useTheme } from '@mui/material'

import { useIsMobile } from 'utils/hooks'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import CustomIcon from 'components_new/atoms/CustomIcon'
import Drawer from 'components_new/atoms/Drawer'
import Fab from 'components_new/atoms/Fab'
import { IconProps } from 'components_new/atoms/Icon'

import EditModeBar from 'components_new/molecules/EditModeBar'
import FixedSideButton from 'components_new/molecules/FixedSideButton'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import SimpleSwipeableDrawer from 'components_new/molecules/SimpleSwipeableDrawer'
import { TBreadcrumbItem } from 'components_new/molecules/Breadcrumbs'

import Topbar from 'components_new/organisms/Topbar'

import { MOBILE_FOOTER_HEIGHT, useMobileNav } from '../GlobalLayout'

export type RightSideBarButtons = {
  icon: IconProps
  onClick: () => void
  title: string
}

export interface PageLayoutProps {
  children: ReactNode
  content?: {
    bgcolor?: string
    disablePadding?: boolean
    editMode?: {
      active: boolean
    }
    fullWidth?: boolean
    hero?: ReactNode
    size?: 'small' | 'medium' | 'large'
  }
  footer?: {
    content?: ReactNode
    disablePadding?: boolean
    height?: number
    mobile?: {
      showNavigation?: boolean
    }
  }
  toolbar?: {
    collapsed?: boolean
    content?: ReactNode
    disablePadding?: boolean
  }
  rightSidebar?: {
    bgcolor?: string
    buttons?: RightSideBarButtons[]
    collapsed?: boolean
    content?: ReactNode
    disablePadding?: boolean
    mobile?: {
      preview?: ReactNode
      title?: string
    }
    navigation?: ReactNode
    pagePadding?: boolean
    setCollapsed?: React.Dispatch<React.SetStateAction<boolean>>
    size?: TSideBarSize
  }
  topbar?: {
    actions?: ReactNode
    breadcrumbs?: TBreadcrumbItem[]
    content?: ReactNode
    mobile?: {
      goBack?: boolean
    }
    options?: OptionProps[]
    title?: ReactNode
  }
  variant: 'feed' | 'static'
}

export const FOOTER_HEIGHT = 40
export const TOOLBAR_HEIGHT = 40
export const TOPBAR_HEIGHT = 48

export const PAGE_PADDING_TOP = 64
export const PAGE_PADDING_BOTTOM = 192

type TSideBarSize = 'small' | 'medium' | 'large' | 'xlarge'

const GridRefContext =
  createContext<React.MutableRefObject<HTMLDivElement | null> | null>(null)

export const useGridRef = () => {
  const context = useContext(GridRefContext)

  if (!context) {
    throw new Error('useGridRef must be used within a GridRefProvider')
  }

  return context
}

const PageLayout = (props: PageLayoutProps) => {
  const { children, content, footer, rightSidebar, toolbar, topbar, variant } =
    props

  const isMobile = useIsMobile()
  const theme = useTheme()
  const gridRef = useRef<HTMLDivElement | null>(null)

  const { showMobileNav, setShowMobileNav } = useMobileNav()
  const shouldShowMobileNav = !!footer?.mobile?.showNavigation

  if (shouldShowMobileNav !== showMobileNav) {
    setShowMobileNav(shouldShowMobileNav)
  }

  const isControlled =
    rightSidebar?.collapsed !== undefined &&
    rightSidebar?.setCollapsed !== undefined

  const [internalRightSidebarOpen, setInternalRightSidebarOpen] =
    useState<boolean>(
      rightSidebar ? !rightSidebar.collapsed && !isMobile : false
    )

  const rightSidebarOpen = isControlled
    ? !rightSidebar.collapsed
    : internalRightSidebarOpen

  const handleToggleRightSidebar = (value?: boolean) => {
    if (isControlled) {
      const next = value !== undefined ? !value : !rightSidebar.collapsed

      if (rightSidebar?.setCollapsed) {
        rightSidebar.setCollapsed(next)
      }
    } else {
      setInternalRightSidebarOpen(
        value !== undefined ? value : !internalRightSidebarOpen
      )
    }
  }

  const getSidebarWidth = (size: TSideBarSize | undefined) => {
    switch (size) {
    case 'small': {
      return 256
    }
    case 'medium': {
      return 384
    }
    case 'large': {
      return 512
    }
    case 'xlarge': {
      return 720
    }
    default: {
      return 384 // medium
    }
    }
  }

  const getContentWidth = () => {
    if (content?.fullWidth) return '100%'

    switch (content?.size) {
    case 'small': {
      return theme.breakpoints.values.sm
    }
    case 'medium': {
      return theme.breakpoints.values.md
    }
    case 'large': {
      return theme.breakpoints.values.lg
    }
    default: {
      if (variant === 'feed') {
        return theme.breakpoints.values.md
      }

      return '100%'
    }
    }
  }

  return (
    <GridRefContext.Provider value={gridRef}>
      <Helmet>
        <meta name="theme-color" content={theme.palette.background.default} />
      </Helmet>

      <Box
        sx={{
          bgcolor: 'background.paper',
          bottom: 0,
          left: 0,
          right: 0,
          position: 'absolute',
          top: 0
        }}
      >
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            position: 'relative',
            width: '100%'
          }}
        >
          <Box
            component="main"
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              mr:
                !rightSidebar || rightSidebarOpen || isMobile
                  ? 0
                  : `-${getSidebarWidth(rightSidebar?.size)}px`,
              minHeight: 0,
              minWidth: 0,
              overflowY: variant === 'feed' ? 'auto' : undefined,
              position: 'relative',
              transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen
              })
            }}
          >
            {topbar ? (
              <Topbar
                actions={<>{topbar.actions}</>}
                breadcrumbs={topbar.breadcrumbs}
                mobile={topbar.mobile}
                options={topbar.options}
                rightSidebar={
                  rightSidebar
                    ? {
                        open: rightSidebarOpen,
                        toggleOpen: handleToggleRightSidebar
                      }
                    : undefined
                }
                title={topbar.title}
              >
                {topbar.content}
              </Topbar>
            ) : null}

            {toolbar?.content && !isMobile ? (
              <Collapse in={!toolbar.collapsed} sx={{ width: '100%' }}>
                <Box
                  sx={{
                    position: variant === 'feed' ? 'sticky' : 'relative',
                    width: '100%',
                    left: 0,
                    right: 0,
                    top: variant === 'feed' ? `${TOPBAR_HEIGHT}px` : 0,
                    height: TOOLBAR_HEIGHT,
                    zIndex: 1,
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(5px)',
                    WebkitBackdropFilter: 'blur(5px)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: 0,
                      px: toolbar.disablePadding ? 0 : 2,
                      py: toolbar.disablePadding ? 0 : 1,
                      right: 0
                    }}
                  >
                    {toolbar.content}
                  </Box>
                </Box>
              </Collapse>
            ) : null}

            {content?.editMode ? (
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  flexShrink: 0,
                  mb: '-1px',
                  position: variant === 'feed' ? 'sticky' : 'relative',
                  width: '100%',
                  left: 0,
                  right: 0,
                  top: variant === 'feed' ? `${TOPBAR_HEIGHT}px` : 0,
                  zIndex: 4
                }}
              >
                {/* Merge with organisms/EditModeBar: */}
                <EditModeBar open={content?.editMode.active} />
              </Box>
            ) : null}

            {variant === 'feed' ? (
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: content?.bgcolor,
                  border: '1px solid',
                  borderColor: content?.editMode?.active
                    ? 'primary.main'
                    : 'transparent',
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  p: 4,
                  pb: content?.disablePadding ? 0 : `${PAGE_PADDING_BOTTOM}px`,
                  ...(!isMobile
                    ? {
                        pt:
                          content?.disablePadding || content?.fullWidth
                            ? 4
                            : `${PAGE_PADDING_TOP}px`
                      }
                    : null),
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    height: 'fit-content',
                    maxWidth: getContentWidth(),
                    position: 'relative',
                    width: '100%'
                  }}
                >
                  {content?.hero ? (
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        pb: 1,
                        position: 'sticky',
                        top: TOPBAR_HEIGHT,
                        zIndex: theme.zIndex.appBar
                      }}
                    >
                      {content.hero}
                    </Box>
                  ) : null}

                  {children}
                </Box>
              </Box>
            ) : (
              <Box sx={{ flex: 1, position: 'relative', width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'absolute',
                    ...(content?.disablePadding
                      ? {
                          bottom: 0,
                          left: 0,
                          right: 0,
                          top: 0
                        }
                      : {
                          bottom: 4 * 8,
                          left: 4 * 8,
                          right: 4 * 8,
                          top: 4 * 8
                        })
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      width: getContentWidth()
                    }}
                  >
                    {content?.hero ? (
                      <Box
                        sx={{
                          bgcolor: 'background.paper',
                          pb: 1,
                          position: 'sticky',
                          top: 0,
                          zIndex: 2
                        }}
                      >
                        {content.hero}
                      </Box>
                    ) : null}

                    {children}
                  </Box>
                </Box>
              </Box>
            )}
            {footer?.content ? (
              <Box
                sx={{
                  position: variant === 'feed' ? 'sticky' : 'relative',
                  width: '100%',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: footer.height ?? FOOTER_HEIGHT,
                  zIndex: 1,
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(5px)',
                  WebkitBackdropFilter: 'blur(5px)',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    px: footer.disablePadding ? 0 : 2,
                    right: 0
                  }}
                >
                  {footer?.content}
                </Box>
              </Box>
            ) : null}

            {rightSidebar?.buttons
              ? rightSidebar.buttons.map((button, i) => (
                <FixedSideButton
                  iconName={button.icon.name}
                  key={i}
                  onClick={button.onClick}
                  tooltip={button.title}
                  top={12 + i * (32 + 4)}
                />
              ))
              : null}
          </Box>

          {rightSidebar && !isMobile ? (
            <Drawer
              anchor="right"
              open={rightSidebarOpen}
              PaperProps={{
                sx: {
                  bgcolor: rightSidebar.bgcolor ?? 'background.default',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: rightSidebar.disablePadding ? 0 : 2,
                  position: 'absolute',
                  pt: rightSidebar.pagePadding
                    ? `${PAGE_PADDING_TOP}px`
                    : undefined,
                  transition: 'all 0.1s',
                  width: getSidebarWidth(rightSidebar?.size),
                  zIndex: 0
                }
              }}
              sx={{
                flexShrink: 0,
                transition: 'all 0.1s',
                width: getSidebarWidth(rightSidebar?.size)
              }}
              variant="persistent"
            >
              {rightSidebar.navigation ? (
                <Box
                  sx={{
                    bgcolor: 'background.default',
                    pb: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2
                  }}
                >
                  {rightSidebar.navigation}
                </Box>
              ) : null}
              {rightSidebar.content}
            </Drawer>
          ) : null}

          {rightSidebar && isMobile ? (
            <>
              {!rightSidebarOpen && rightSidebar?.mobile?.preview ? (
                <Box
                  sx={{
                    position: 'fixed',
                    bottom: MOBILE_FOOTER_HEIGHT + 16,
                    right: 72,
                    height: 56,
                    width: 'calc(100% - 72px)'
                  }}
                >
                  {rightSidebar.mobile.preview}
                </Box>
              ) : null}

              <ThemeProvider theme={theme}>
                <Fab
                  color="default"
                  onClick={() => handleToggleRightSidebar(true)}
                  sx={{
                    position: 'fixed',
                    right: 16,
                    bottom: (footer?.content ? MOBILE_FOOTER_HEIGHT : 0) + 16
                  }}
                >
                  <CustomIcon name="MobileBottomDrawer" />
                </Fab>
              </ThemeProvider>

              <SimpleSwipeableDrawer
                onClose={() => handleToggleRightSidebar(false)}
                onOpen={() => handleToggleRightSidebar(true)}
                open={rightSidebarOpen}
                title={rightSidebar.mobile?.title}
              >
                {rightSidebar.navigation}
                {rightSidebar.content}
              </SimpleSwipeableDrawer>
            </>
          ) : null}
        </Box>
      </Box>
    </GridRefContext.Provider>
  )
}

PageLayout.displayName = 'PageLayout'
export default PageLayout
